/**
 * ContentContext
 *
 * Loads org-specific content from the API (OrganizationText table) and provides:
 *   - c(sectionName)           → raw text for current language
 *   - resolveContentValue(val) → live/draft content references resolved for rendering
 *   - updateContent(key, val, lang) → stage a change to DraftContext
 *   - saveContent()            → persist draft to localStorage (no longer pushes to API directly)
 *   - pendingContentChanges    → array of staged changes (derived from DraftContext for compat)
 *   - contentLoading           → true while fetching
 *
 * In Draft/Live mode, edits go to DraftContext and only push to the API on Publish.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import { useLanguage } from './LanguageContext';
import { useDraft } from './DraftContext';
import { resolveContentReferences } from '../utils/contentReferences';

const LANG_MAP = { en: 'English', fr: 'French', es: 'Spanish' };

/**
 * Decode HTML entities for accented characters so language detection works
 * even when the content uses HTML-escaped forms like &eacute; → é.
 */
const decodeAccentEntities = s => (s || '')
  .replace(/&agrave;/gi, 'à').replace(/&aacute;/gi, 'á').replace(/&acirc;/gi, 'â')
  .replace(/&ccedil;/gi, 'ç')
  .replace(/&egrave;/gi, 'è').replace(/&eacute;/gi, 'é').replace(/&ecirc;/gi, 'ê').replace(/&euml;/gi, 'ë')
  .replace(/&igrave;/gi, 'ì').replace(/&iacute;/gi, 'í').replace(/&icirc;/gi, 'î').replace(/&iuml;/gi, 'ï')
  .replace(/&ntilde;/gi, 'ñ')
  .replace(/&ograve;/gi, 'ò').replace(/&oacute;/gi, 'ó').replace(/&ocirc;/gi, 'ô').replace(/&ouml;/gi, 'ö').replace(/&oslash;/gi, 'ø')
  .replace(/&ugrave;/gi, 'ù').replace(/&uacute;/gi, 'ú').replace(/&ucirc;/gi, 'û').replace(/&uuml;/gi, 'ü')
  .replace(/&yuml;/gi, 'ÿ').replace(/&yacute;/gi, 'ý')
  .replace(/&oelig;/gi, 'œ').replace(/&aelig;/gi, 'æ').replace(/&aring;/gi, 'å')
  .replace(/&#(\d+);/g, (_, n) => { try { return String.fromCharCode(Number(n)); } catch { return ''; } });

const LANG_CHAR_PATTERNS = {
  French:  /[àâçéèêëîïôùûüÿœæÀÂÇÉÈÊËÎÏÔÙÛÜŸŒÆ]/,
  Spanish: /[áéíóúñüÁÉÍÓÚÑÜ¡¿]/,
};

/**
 * Returns true if the content appears to actually be in the given DB language.
 * For French/Spanish, we check for language-specific accented characters.
 * Content that passes through the backend's "English fallback for missing translations"
 * will have no accented characters and will return false.
 */
const appearsTranslated = (text, dbLang) => {
  const pattern = LANG_CHAR_PATTERNS[dbLang];
  if (!pattern) return true; // Unknown lang — don't second-guess it
  return pattern.test(decodeAccentEntities(text));
};

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const { language } = useLanguage();
  const { tlfId } = getConfig();
  const draft = useDraft();

  // content[lang][SectionName] = text  (live API data)
  const [content, setContent] = useState({ English: {}, French: {}, Spanish: {} });
  const [contentLoading, setContentLoading] = useState(true);
  const [contentLoadedLanguages, setContentLoadedLanguages] = useState({});

  const fetchContent = useCallback(async () => {
    if (!tlfId) return;
    setContentLoading(true);
    try {
      const dbLang = LANG_MAP[language] || 'English';
      const langsToFetch = dbLang === 'English'
        ? ['English']
        : [dbLang, 'English'];

      const results = await Promise.all(
        langsToFetch.map(lang =>
          api.get(apiRoutes.OrganizationContent, {
            params: { organizationId: tlfId, lang },
          }).then(r => {
            const body = r.data;
            if (!body || typeof body !== 'object' || Array.isArray(body)) return { lang, data: {} };
            if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
              return { lang, data: body.data };
            }
            return { lang, data: body };
          })
            .catch(() => ({ lang, data: {} }))
        )
      );
      setContent(prev => {
        const next = { ...prev };
        results.forEach(({ lang, data }) => { next[lang] = data; });
        return next;
      });
      setContentLoadedLanguages(prev => {
        const next = { ...prev };
        results.forEach(({ lang }) => { next[lang] = true; });
        return next;
      });
    } catch {
      // fall through — content falls back to hardcoded defaults
    } finally {
      setContentLoading(false);
    }
  }, [tlfId, language]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // After a draft is published the server-side live content changes.
  // Strategy:
  //   1. Re-fetch from the API (Redis cache was busted by publish).
  //   2. In the SAME setContent call, merge the published content on top of
  //      the fetched data — so the admin's saved values are authoritative and
  //      cannot be overwritten by a subsequent fetchContent call.
  // This is done in one atomic setContent to avoid the race where a separate
  // merge is overwritten by the fetch's own setContent.
  useEffect(() => {
    const handlePublished = async (evt) => {
      const publishedContent = evt?.detail?.publishedContent;
      if (!tlfId) return;

      try {
        const dbLang = LANG_MAP[language] || 'English';
        const langsToFetch = dbLang === 'English' ? ['English'] : [dbLang, 'English'];
        const results = await Promise.all(
          langsToFetch.map(lang =>
            api.get(apiRoutes.OrganizationContent, {
              params: { organizationId: tlfId, lang },
            }).then(r => {
              const body = r.data;
              if (!body || typeof body !== 'object' || Array.isArray(body)) return { lang, data: {} };
              if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
                return { lang, data: body.data };
              }
              return { lang, data: body };
            }).catch(() => ({ lang, data: {} }))
          )
        );

        setContent(prev => {
          const next = { ...prev };
          // Apply fresh API data
          results.forEach(({ lang, data }) => {
            next[lang] = { ...(next[lang] || {}), ...data };
          });
          // Merge published content ON TOP — authoritative, bypasses appearsTranslated
          if (publishedContent && typeof publishedContent === 'object') {
            for (const [lang, entries] of Object.entries(publishedContent)) {
              if (entries && typeof entries === 'object') {
                next[lang] = { ...(next[lang] || {}), ...entries };
              }
            }
          }
          return next;
        });
      } catch {
        // Fetch failed — fall back to just applying the published content
        if (publishedContent && typeof publishedContent === 'object') {
          setContent(prev => {
            const next = { ...prev };
            for (const [lang, entries] of Object.entries(publishedContent)) {
              if (entries && typeof entries === 'object') {
                next[lang] = { ...(next[lang] || {}), ...entries };
              }
            }
            return next;
          });
        }
      }
    };
    window.addEventListener('draftPublished', handlePublished);
    return () => window.removeEventListener('draftPublished', handlePublished);
  }, [fetchContent, tlfId, language]);

  const getContentValue = useCallback((sectionName, lang = language) => {
    const dbLang = LANG_MAP[lang] || lang || 'English';

    // Check draft first (when admin or in preview mode)
    if (draft.shouldShowDraft) {
      const draftVal = draft.getDraftContent(sectionName, dbLang);
      if (draftVal !== undefined) return draftVal;
    }

    return content[dbLang]?.[sectionName]
      ?? content['English']?.[sectionName]
      ?? undefined;
  }, [content, language, draft]);

  /**
   * Look up a SectionName — draft overlay → live API (current language) →
   * provided fallback (usually a localized translation) → English DB content.
   *
   * For non-English languages the provided fallback takes priority over
   * English DB content, so org edits made in English don't override
   * proper translations from translations.json.
   */
  const c = useCallback((sectionName, fallback = '') => {
    const dbLang = LANG_MAP[language] || 'English';

    if (draft.shouldShowDraft) {
      const draftVal = draft.getDraftContent(sectionName, dbLang);
      if (draftVal !== undefined) return draftVal;
    }

    const localized = content[dbLang]?.[sectionName];
    if (localized !== undefined) {
      // `appearsTranslated` detects when the DB returned English platform-default
      // text for a non-English language (no locale-specific characters present).
      // We ONLY apply this filter when the value is IDENTICAL to a known English
      // DB value — meaning it was not translated, it just echoed the English row.
      // If englishValue is undefined (no English org override), the French value
      // is org-specific and must be honoured unconditionally (even without accents).
      const englishValue = content['English']?.[sectionName];
      const isLikelyPlatformDefault =
        englishValue !== undefined && localized === englishValue;
      if (
        dbLang !== 'English' &&
        fallback &&
        !appearsTranslated(localized, dbLang) &&
        isLikelyPlatformDefault
      ) {
        return fallback;
      }
      return localized;
    }

    if (dbLang !== 'English' && fallback) return fallback;

    return content['English']?.[sectionName] ?? fallback;
  }, [content, language, draft]);

  const resolveContentValue = useCallback((value, options = {}) => {
    return resolveContentReferences(value, key => getContentValue(key), {
      currentKey: options.currentKey,
      editMode: options.editMode ?? draft.shouldShowDraft,
    });
  }, [draft.shouldShowDraft, getContentValue]);

  const getContentOptions = useCallback(({ excludeKey } = {}) => {
    const dbLang = LANG_MAP[language] || 'English';
    const keys = new Set([
      ...Object.keys(content['English'] || {}),
      ...Object.keys(content[dbLang] || {}),
      ...Object.keys(draft.draftContent?.['English'] || {}),
      ...Object.keys(draft.draftContent?.[dbLang] || {}),
    ]);

    return [...keys]
      .filter(key => key && key !== excludeKey)
      .map(key => {
        const draftValue = draft.shouldShowDraft ? draft.getDraftContent(key, dbLang) : undefined;
        const value = getContentValue(key, dbLang);
        const source = draftValue !== undefined
          ? 'draft'
          : content[dbLang]?.[key] !== undefined
            ? dbLang
            : 'English';

        return {
          key,
          value,
          source,
          isContentBlock: /^section\.content_block_\d+\.text$/.test(key),
        };
      })
      .filter(option => String(option.value || '').trim().length > 0)
      .sort((a, b) => {
        if (a.isContentBlock !== b.isContentBlock) return a.isContentBlock ? -1 : 1;
        return a.key.localeCompare(b.key);
      });
  }, [content, draft, getContentValue, language]);

  /** Stage a content change → DraftContext (no longer pushes to API). */
  const updateContent = useCallback((sectionName, value, lang) => {
    const dbLang = LANG_MAP[lang] || LANG_MAP[language] || 'English';
    draft.updateDraftContent(sectionName, value, dbLang);
  }, [language, draft]);

  /** Persist draft to localStorage. For backward compat, callers still invoke saveContent(). */
  const saveContent = useCallback(async () => {
    draft.saveDraft();
  }, [draft]);

  // Build a pendingContentChanges array from draftContent for backward compatibility
  const pendingContentChanges = [];
  if (draft.draftContent) {
    for (const [lang, entries] of Object.entries(draft.draftContent)) {
      for (const [sectionName, value] of Object.entries(entries || {})) {
        pendingContentChanges.push({ sectionName, value, language: lang });
      }
    }
  }

  return (
    <ContentContext.Provider value={{
      c,
      content,
      contentLoadedLanguages,
      contentLoading,
      getContentValue,
      getContentOptions,
      resolveContentValue,
      updateContent,
      saveContent,
      pendingContentChanges,
      setPendingContentChanges: () => {}, // no-op for compat
      refetchContent: fetchContent,
    }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within a ContentProvider');
  return ctx;
}

export function useOptionalContent() {
  return useContext(ContentContext);
}
